import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { TotemWidget } from '../totem-widget';
import { TravelDataService } from 'pg-lib/services/travel-data.service';
import { TravelChallengeStatus } from 'pg-lib/models/travel.model';

export type TotemWidgetChallengeItem = {
    mission: string;
    objective: string;
    type?: 'photo'|'question'|'multiple_choice'|'checkin';
    checked?: boolean;
    answer?: string;
    id?: string;
    destination?: string;
    resultKey?: string;
    points?: number;
    options?: string[];
    correctAnswer?: string;
    explanation?: string;
};

export type TotemWidgetChallengeLocationGroup = {
    destination: string;
    challenges: Array<TotemWidgetChallengeItem>;
    challengeIndex: number;
};

@Component({
    selector: 'app-totem-widget-challenge',
    templateUrl: './totem-widget-challenge.component.html',
    styleUrls: ['./totem-widget-challenge.component.scss'],
    standalone: false
})
export class TotemWidgetChallengeComponent extends TotemWidget implements OnInit, OnChanges {
    size: 'md' = 'md';

    @ViewChild('locationsList', { static: false }) locationsList: ElementRef<HTMLElement>;

    @Input() challengeList: Array<TotemWidgetChallengeItem>;
    @Input() editable: boolean;

    @Input() country: string;
    @Input() viaggiareSicuriData: any;

    /** When set, challenges sync with travel challenge-results API. */
    @Input() travelId: number;
    @Input() useTravelChallenges: boolean;

    /** When set (e.g. by tour-day selector), scrolls to matching challenge location. */
    @Input() preferredDestination: string;

    selectedDestination: string = null;
    destinationOptions: Array<{ value: string; text: string }> = [];
    locationGroups: Array<TotemWidgetChallengeLocationGroup> = [];

    constructor(public elementRef: ElementRef, private travelDataService: TravelDataService) {
        super(elementRef);
    }

    ngOnInit(): void {
        super.ngOnInit();

        if (this.useTravelChallenges && this.travelDataService.isEnabled()) {
            const fromTravel = this.travelDataService.challengesToChallengeList();
            if (fromTravel.length) {
                this.challengeList = fromTravel;
            }
            if (this.travelId == null) {
                this.travelId = this.travelDataService.currentTravelId;
            }
            this._initDestinations();
            return;
        }

        if (!this.challengeList) {
            this.challengeList = [];
            this._initDestinations();
            return;
        }

        const savedData = localStorage.getItem('DEMOTOTEM-' + this.id);

        if (savedData != null) {
            const checkedList: Array<string> = JSON.parse(savedData);

            for (const item of this.challengeList) {
                item.checked = checkedList.indexOf(item.mission) != -1;
            }
        }

        this._initDestinations();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['preferredDestination'] && this.preferredDestination) {
            this.setDestination(this.preferredDestination);
        }
    }

    private _initDestinations() {
        const destinations: string[] = [];
        for (const item of this.challengeList || []) {
            if (item.destination && destinations.indexOf(item.destination) === -1) {
                destinations.push(item.destination);
            }
        }

        const orderedDestinations = this._sortDestinationsByProgram(destinations);

        this.destinationOptions = orderedDestinations.map((destination) => ({
            value: destination,
            text: destination,
        }));

        this.locationGroups = orderedDestinations.map((destination) => ({
            destination,
            challenges: (this.challengeList || []).filter((item) => item.destination === destination),
            challengeIndex: 0,
        }));

        // Challenges without destination stay in a single fallback group.
        const unassigned = (this.challengeList || []).filter((item) => !item.destination);
        if (unassigned.length) {
            this.locationGroups.push({
                destination: '',
                challenges: unassigned,
                challengeIndex: 0,
            });
        }

        if (!this.selectedDestination || orderedDestinations.indexOf(this.selectedDestination) === -1) {
            this.selectedDestination = orderedDestinations[0] || this.locationGroups[0]?.destination || null;
        }
    }

    /** Keep challenge location cards in the same order as program locations. */
    private _sortDestinationsByProgram(destinations: string[]): string[] {
        if (!destinations.length) return destinations;

        const programLocations = (this.travelDataService.isEnabled()
            ? this.travelDataService.programToActivityList()
            : [])
            .map((day) => day.location)
            .filter((location): location is string => !!location);

        if (!programLocations.length) return destinations;

        const remaining = destinations.slice();
        const ordered: string[] = [];

        for (const programLocation of programLocations) {
            const match = this._matchDestinationByDayLocation(programLocation, remaining);
            if (!match) continue;

            ordered.push(match);
            remaining.splice(remaining.indexOf(match), 1);
            if (!remaining.length) break;
        }

        return ordered.concat(remaining);
    }

    private _matchDestinationByDayLocation(dayLocation: string, destinations: string[]): string | null {
        const needle = dayLocation?.trim();
        if (!needle || !destinations.length) return null;

        const needleLower = needle.toLowerCase();
        for (const value of destinations) {
            if (value === needle || value.toLowerCase() === needleLower) return value;
        }
        for (const value of destinations) {
            if (value.toLowerCase().endsWith(needleLower)) return value;
        }
        return null;
    }

    /** Select an exact challenge destination (already resolved by the caller). */
    setDestination(destination: string) {
        if (destination == null || destination === '') return;

        this.selectedDestination = destination;
        this.scrollToDestination(destination);
    }

    scrollToDestination(destination: string) {
        if (destination == null) return;

        const run = () => {
            const list = this.locationsList?.nativeElement;
            if (!list) return;

            const children = Array.from(list.children) as HTMLElement[];
            const target = children.find((el) => el.dataset.destination === destination);
            if (!target) return;

            const gutter = 15;
            const left = list.scrollLeft
                + (target.getBoundingClientRect().left - list.getBoundingClientRect().left)
                - gutter;
            list.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        };

        setTimeout(run, 0);
    }

    shiftChallenge(group: TotemWidgetChallengeLocationGroup, offset: number) {
        const total = group.challenges.length;
        if (!total) return;

        let next = group.challengeIndex + offset;
        if (next < 0) next = 0;
        if (next > total - 1) next = total - 1;
        group.challengeIndex = next;
    }

    getCompletedCount(group?: TotemWidgetChallengeLocationGroup) {
        const list = group ? group.challenges : this.filteredChallenges;
        let count = 0;
        for (const item of list) {
            if (item.checked) count++;
        }
        return count;
    }

    get filteredChallenges(): Array<TotemWidgetChallengeItem> {
        if (!this.selectedDestination) {
            return this.challengeList || [];
        }
        return (this.challengeList || []).filter((item) => item.destination === this.selectedDestination);
    }

    isAllCompleted(group: TotemWidgetChallengeLocationGroup) {
        return group.challenges.length > 0 && this.getCompletedCount(group) == group.challenges.length;
    }

    getCircleStrokeDashArray(group: TotemWidgetChallengeLocationGroup) {
        const total = group.challenges.length;
        if (total == 0) return '0,1';

        const perc = this.getCompletedCount(group) / total;
        const radius = ((160 - 16) / 2);

        return (perc * radius * Math.PI * 2) + ',' + ((1 - perc) * radius * Math.PI * 2);
    }

    openChallenge(group: TotemWidgetChallengeLocationGroup, index: number) {
        const challenge = group.challenges[index];
        if (!challenge) return;

        const globalIndex = (this.challengeList || []).indexOf(challenge);
        if (globalIndex < 0) return;

        this.selectedDestination = group.destination || this.selectedDestination;
        group.challengeIndex = index;
        this.requestFullScreen.emit({ params: globalIndex });
    }

    completeChallenge(challenge: TotemWidgetChallengeItem, answer?: string, correct?: boolean) {
        if (answer != null) {
            challenge.answer = answer;
        }
        challenge.checked = true;
        this.onCompletedChange(challenge, correct);
    }

    onCompletedChange(challenge?: TotemWidgetChallengeItem, correct?: boolean) {
        if (this.travelDataService.isEnabled() && this.travelId != null && this.challengeList?.some((item) => item.id)) {
            const results = this.challengeList
                .filter((item) => item.id && item.destination)
                .map((item) => {
                    const isCurrent = challenge && item === challenge;
                    return {
                        destination: item.destination,
                        challenge_id: item.id,
                        status: (item.checked ? 'completed' : 'pending') as TravelChallengeStatus,
                        answer: item.answer,
                        points: item.points,
                        correct: isCurrent ? correct : undefined,
                    };
                });

            this.travelDataService.updateChallengeResults(this.travelId, {
                results,
                replace: false,
            }).subscribe({
                error: (err) => console.warn('Travel challenge results save failed', err),
            });
            return;
        }

        const checkedList = [];

        for (const item of this.challengeList || []) {
            if (item.checked) checkedList.push(item.mission);
        }

        localStorage.setItem('DEMOTOTEM-' + this.id, JSON.stringify(checkedList));
    }

    goToNextChallenge() {
        const currentIndex = Number(this.fullScreenParams);
        const list = this.challengeList || [];
        const current = list[currentIndex];
        if (!current) {
            this.hideFullScreen.emit();
            return;
        }

        const group = this.locationGroups.find((item) => item.destination === (current.destination || ''));
        const groupIndex = group ? group.challenges.indexOf(current) : -1;

        if (group && groupIndex >= 0 && groupIndex + 1 < group.challenges.length) {
            const next = group.challenges[groupIndex + 1];
            group.challengeIndex = groupIndex + 1;
            this.requestFullScreen.emit({ params: list.indexOf(next) });
            return;
        }

        this.hideFullScreen.emit();
    }

    getChallengeNumber(challenge: TotemWidgetChallengeItem) {
        const destination = challenge.destination || '';
        const group = this.locationGroups.find((item) => item.destination === destination);
        if (!group) return 1;
        const index = group.challenges.indexOf(challenge);
        return index >= 0 ? index + 1 : 1;
    }

    getChallengeIcon(challenge: TotemWidgetChallengeItem) {
        if (challenge.type === 'photo') return 'camera';
        if (challenge.type === 'checkin') return 'location-dot';
        if (challenge.type === 'multiple_choice') return 'list-check';
        return 'pen-to-square';
    }
}
